import {
    Box,
    CSSObject,
    Divider,
    Drawer,
    IconButton,
    List,
    styled,
    Theme,
} from "@mui/material";
import React from "react";
import { useAppSelector } from "../redux/hooks";
import { SidebarLinkObject, SidebarParentObject } from "../typeDefs/common";
import Footer from "./common/Footer";
import ExpandableSidebarLink from "./common/utils/ExpandableSidebarLink";
import PrivateSidebarLink from "./common/utils/PrivateSidebarLink";
import { useHistory, useLocation } from "react-router";

const openedMixin = (theme: Theme, drawerWidth: number): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up("sm")]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const StyledDrawer = styled(Drawer, {
    shouldForwardProp: (prop) =>
        !["open", "drawerWidth"].includes(prop.toString()),
})<{
    open: boolean;
    drawerWidth: number;
}>(({ theme, open, drawerWidth }) => ({
    width: drawerWidth,
    height: "100vh",
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    msOverflowStyle: "none",
    scrollbarWidth: "none",
    ".MuiList-root": {
        overflowX: "visible",
    },
    ".linkList": {
        transition: "0.3s",
        marginTop: "12px",
        minHeight: "0",
        flexShrink: "0",
        flexBasis: "0",
        flexGrow: "1",
        overflow: "visible",
        scrollbarWidth: "none",
    },
    ".linkList::-webkit-scrollbar": {
        display: "none",
    },
    ".MuiPaper-root": {
        background: theme.palette.gradient!.dark,
        color: theme.palette.primary.contrastText,
        paddingLeft: 8,
        paddingRight: open ? 18 : 8,
        paddingTop: "32px",
        border: "none",
        height: "100%",
        // marginTop: navbarHeight,
        msOverflowStyle: "none",
        scrollbarWidth: "none",
        maxHeight: "100vh",
        borderRadius: "0px",
        "::-webkit-scrollbar": {
            display: "none",
        },
        ...(open
            ? {
                  ...openedMixin(theme, drawerWidth),
              }
            : {
                  ...closedMixin(theme),
              }),
    },
    ...(open
        ? {
              ...openedMixin(theme, drawerWidth),
          }
        : {
              ...closedMixin(theme),
          }),
}));

const ListContainer = styled(Box)(({ theme }) => ({
    flex: 1,
    width: "calc(100% + 16px)",
    marginLeft: "-8px",
    marginRight: "-8px",
    "&::-webkit-scrollbar": {
        display: "none",
    },
}));

const ImageContainer = styled("div")(() => ({
    width: "100%",
    padding: 8,
    textAlign: "center",
    ".dfLogo": {
        color: "rgba(255, 255, 255, 1)",
        height: "48px",
        objectFit: "contain",
    },
    ".MuiSvgIcon-root": {
        height: "45px !important",
        marginBottom: "25px",
    },
}));

interface Props {
    drawerWidth: number;
    links: (SidebarLinkObject | SidebarParentObject)[];
}

const SideBar = ({ drawerWidth, links }: Props) => {
    const { sidebarOpen: open } = useAppSelector((state) => state.common);

    const [expandedMenu, setExpandedMenu] = React.useState("");
    const [imageUrl, setImageUrl] = React.useState(
        `${window.location.origin}/images/Image20230404121016.png`
    );

    const history = useHistory();

    const renderLink = (link: SidebarLinkObject | SidebarParentObject) => {
        if ("route" in link) {
            return (
                <PrivateSidebarLink
                    label={link.label}
                    route={link.route}
                    icon={link.icon}
                    hideIfLockedDown={link.hideIfLockedDown}
                    displayFlag={link.displayFlag}
                    requiredPython={link.requiredPython}
                    userTypes={link.userTypes}
                    permission={link.permission}
                    exact={link.exact}
                />
            );
        } else if ("links" in link) {
            const sidebarLink = (
                <ExpandableSidebarLink
                    order={link.order}
                    links={link.links}
                    label={link.label}
                    icon={link.icon}
                    hideIfLockedDown={link.hideIfLockedDown}
                    displayFlag={link.displayFlag}
                    userTypes={link.userTypes}
                    permissions={link.permissions}
                    open={expandedMenu === link.label}
                    setOpenMenu={() =>
                        setExpandedMenu(
                            expandedMenu === link.label ? "" : link.label
                        )
                    }
                />
            );
            return sidebarLink;
        }
    };

    const redirect = () => {
        history.replace("/");
    };

    React.useEffect(() => {
        if (open) {
            setImageUrl(
                `${window.location.origin}/images/Image20230404121016.png`
            );
        } else {
            setTimeout(() => {
                setImageUrl(
                    `${window.location.origin}/images/logo-white-no-words.svg`
                );
            }, 200);
        }
    }, [open]);

    return (
        <StyledDrawer open={open} drawerWidth={drawerWidth} variant="permanent">
            <ImageContainer onClick={redirect}>
                <img src={imageUrl} alt="DF Logo" className="dfLogo" />
                {/* {open ? (
                    <img
                        src={`${window.location.origin}/images/DF-white-horizontal.png`}
                        alt="DF Logo"
                        className="dfLogo"
                    />
                ) : (
                    <DFSmall className="dfLogo" fontSize="large" />
                )} */}
            </ImageContainer>
            <ListContainer>
                <List className="linkList" dense>
                    {links.map(renderLink)}
                </List>
            </ListContainer>
            <Footer />
        </StyledDrawer>
    );
};

export default SideBar;
